import React from 'react';
import { Package, Truck, Clock, MapPin, CheckCircle, AlertCircle } from 'lucide-react';
import Header from '../../components/common/Header';
import Footer from '../../components/common/Footer';

const ShippingPolicy = () => {
  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute transform rotate-45 -left-20 -top-20 w-96 h-96 bg-white rounded-full"></div>
            <div className="absolute transform -rotate-12 right-0 top-40 w-72 h-72 bg-white rounded-full"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Truck className="w-4 h-4" />
                <span className="text-sm font-medium">Delivery Information</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
                Shipping & Delivery Policy
              </h1>
              <p className="text-lg md:text-xl text-indigo-100 max-w-2xl mx-auto">
                Fast, reliable delivery of medicines and healthcare products to your doorstep
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 space-y-8">
            <p className="text-gray-600 text-sm">
              <strong>Effective Date:</strong> January 1, 2025
            </p>

            {/* Delivery Coverage */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">1. Delivery Coverage</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>1.1 Service Areas</strong></p>
                <p>We currently provide delivery services across major cities and towns in India. Delivery availability and timelines may vary based on your location.</p>

                <p><strong>1.2 Serviceability Check</strong></p>
                <p>You can check delivery availability for your area by entering your pincode during checkout. If your area is not serviceable, we will notify you before order confirmation.</p>
              </div>
            </section>

            {/* Delivery Timeline */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">2. Delivery Timeline</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>2.1 Standard Delivery</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Medicines & Healthcare Products:</strong> 24-48 hours from order confirmation</li>
                  <li><strong>Prescription Medicines:</strong> 24-48 hours after prescription verification</li>
                  <li><strong>OTC & Wellness Products:</strong> 24-48 hours</li>
                  <li><strong>Medical Equipment:</strong> 2-5 business days depending on product availability</li>
                </ul>

                <p className="pt-3"><strong>2.2 Express Delivery</strong></p>
                <p>Available in select locations for urgent medicine requirements:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Same-day delivery (if ordered before 2:00 PM)</li>
                  <li>4-6 hour delivery window (additional charges apply)</li>
                  <li>Available for medicines and essential healthcare products only</li>
                </ul>

                <p className="pt-3"><strong>2.3 Service Delivery</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Sample Collection:</strong> Within 24 hours of booking</li>
                  <li><strong>Nursing/Paramedical Services:</strong> As per scheduled time slot</li>
                  <li><strong>Ambulance Services:</strong> 10-30 minutes response time (emergency basis)</li>
                </ul>
              </div>
            </section>

            {/* Delivery Charges */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">3. Delivery Charges</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>3.1 Free Delivery</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Orders above ₹500: FREE standard delivery</li>
                  <li>Sample collection for diagnostic tests: FREE</li>
                  <li>Prescription medicine orders: FREE standard delivery</li>
                </ul>

                <p className="pt-3"><strong>3.2 Standard Delivery Charges</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Orders below ₹500: ₹40 delivery charge</li>
                  <li>Remote/distant locations: Additional ₹20-50 (if applicable)</li>
                </ul>

                <p className="pt-3"><strong>3.3 Express Delivery Charges</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Same-day delivery: ₹100 additional charge</li>
                  <li>4-6 hour delivery: ₹150 additional charge</li>
                </ul>
              </div>
            </section>

            {/* Order Processing */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">4. Order Processing</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>4.1 Order Confirmation</strong></p>
                <p>Once your order is placed and payment is confirmed, you will receive an order confirmation via SMS and email.</p>

                <p><strong>4.2 Prescription Verification (For Prescription Medicines)</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Upload a valid prescription during checkout</li>
                  <li>Our pharmacist will verify the prescription within 2-4 hours</li>
                  <li>Order processing begins after successful verification</li>
                  <li>If prescription is unclear or invalid, we will contact you for clarification</li>
                </ul>

                <p><strong>4.3 Order Preparation</strong></p>
                <p>Your order is carefully prepared, quality-checked, and securely packed at our fulfillment center.</p>

                <p><strong>4.4 Dispatch Notification</strong></p>
                <p>You will receive an SMS/email notification once your order is dispatched, along with tracking details (if available).</p>
              </div>
            </section>

            {/* Delivery Process */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-blue-500 rounded-lg flex items-center justify-center">
                  <Truck className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">5. Delivery Process</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>5.1 Home Delivery</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Our delivery partner will deliver the order to your registered address</li>
                  <li>Signature/OTP verification may be required for high-value orders</li>
                  <li>Please ensure someone is available to receive the delivery</li>
                </ul>

                <p className="pt-3"><strong>5.2 Contactless Delivery</strong></p>
                <p>We offer contactless delivery where the package is left at your doorstep upon request. Please mention this preference in delivery instructions.</p>

                <p><strong>5.3 Failed Delivery Attempts</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>If delivery fails due to unavailability, our partner will make up to 2 additional attempts</li>
                  <li>You will be contacted to reschedule delivery</li>
                  <li>If all attempts fail, the order may be returned to us, and you may be eligible for a refund (minus applicable charges)</li>
                </ul>
              </div>
            </section>

            {/* Special Handling */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">6. Special Handling for Medicines</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>6.1 Temperature-Sensitive Products</strong></p>
                <p>Medicines requiring refrigeration or special storage are delivered in temperature-controlled packaging to maintain efficacy.</p>

                <p><strong>6.2 Quality Assurance</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>All medicines are sourced from licensed distributors</li>
                  <li>Expiry dates are checked before dispatch (minimum 6 months validity)</li>
                  <li>Tamper-proof packaging ensures product safety</li>
                </ul>
              </div>
            </section>

            {/* Tracking */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">7. Order Tracking</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>7.1 Real-Time Tracking</strong></p>
                <p>Track your order status in real-time through:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>HomeCure mobile app or website (My Orders section)</li>
                  <li>SMS/email notifications at each stage</li>
                  <li>Customer support helpline</li>
                </ul>

                <p><strong>7.2 Order Stages</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Order Placed</li>
                  <li>Prescription Verified (if applicable)</li>
                  <li>Order Confirmed</li>
                  <li>Packing in Progress</li>
                  <li>Dispatched</li>
                  <li>Out for Delivery</li>
                  <li>Delivered</li>
                </ul>
              </div>
            </section>

            {/* Replacement Only */}
            <section>
              <div className="bg-gradient-to-r from-orange-50 to-red-50 border-l-4 border-orange-500 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">8. Replacement Policy (No Returns)</h3>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      As per healthcare and pharmaceutical regulations, <strong>we do not accept returns for medicines and medical products</strong>. However, we offer replacements in the following cases:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-gray-700">
                      <li><strong>Wrong Item Delivered:</strong> If you received a different product than ordered</li>
                      <li><strong>Damaged/Defective Product:</strong> If the product arrived damaged or defective</li>
                      <li><strong>Expired or Near-Expiry Products:</strong> If products have less than 6 months validity</li>
                      <li><strong>Quality Issues:</strong> Manufacturing defects or tampering</li>
                    </ul>
                    <p className="text-gray-700 leading-relaxed mt-3">
                      <strong>Replacement Process:</strong> Contact customer support within 48 hours of delivery with photos/proof. We will arrange a replacement at no additional cost after verification.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Contact */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">9. Delivery Support</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p>For any delivery-related queries or issues, please contact us:</p>
                <div className="bg-gray-50 rounded-lg p-4 mt-3">
                  <p><strong>HomeCure Customer Support</strong></p>
                  <p>📧 Email: support@homecure.com</p>
                  <p>📞 Phone: +91 98765 43210</p>
                  <p>🕒 Hours: 24/7 Support Available</p>
                  <p>🌐 Website: www.homecure.com</p>
                </div>
              </div>
            </section>

            <div className="border-t pt-6 mt-8">
              <p className="text-sm text-gray-500">
                <strong>Last Updated:</strong> January 1, 2025
              </p>
              <p className="text-sm text-gray-600 mt-3">
                HomeCure reserves the right to update this Shipping & Delivery Policy at any time. Changes will be communicated via email or website notification.
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ShippingPolicy;
